import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { 
  RiSendPlaneLine, 
  RiMessage2Line, 
  RiUserLine, 
  RiCustomerServiceLine,
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiCalendarLine
} from "react-icons/ri";

const FlightAmendmentModal = ({ show, booking, onClose, onSuccess, user }) => {
  const [activeTab, setActiveTab] = useState("new"); // "new" or "history"
  const [amendmentRequest, setAmendmentRequest] = useState({
    requestType: "",
    requestContent: "",
  });
  const [existingAmendments, setExistingAmendments] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [expandedId, setExpandedId] = useState(null);

  const [replyTexts, setReplyTexts] = useState({});
  const [sendingReplies, setSendingReplies] = useState({});

  const getAmendmentHistory = async () => {
    if (!booking?.flightBookingId) return;
    setLoadingHistory(true);
    try {
      const endpoint = `${config.GETFLIGHTAMENDMENTBYFLIGHTBOOKINGID}/${booking.flightBookingId}`;
      const response = await apiService.getAll(endpoint);
      
      if (response && response.data) {
        setExistingAmendments(response.data);
      } else if (Array.isArray(response)) {
        setExistingAmendments(response);
      }
    } catch (error) {
      console.error("Error fetching amendment history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (show && booking) {
      setAmendmentRequest({
        requestType: "",
        requestContent: "",
      });
      setReplyTexts({});
      setSendingReplies({});
      setExpandedId(null);
      setActiveTab("new");
      getAmendmentHistory();
    }
  }, [show, booking]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAmendmentRequest(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleReplyChange = (amendId, text) => {
    setReplyTexts(prev => ({
      ...prev,
      [amendId]: text
    }));
  };

  const toggleExpand = (amendId) => {
    setExpandedId(prev => (prev === amendId ? null : amendId));
  };

  const handleSubmit = async () => {
    if (!amendmentRequest.requestType) {
      alert("Please select request type");
      return;
    }
    if (!amendmentRequest.requestContent) {
      alert("Please enter request content");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        flightBookingId: booking.flightBookingId,
        requestType: amendmentRequest.requestType,
        requestContent: amendmentRequest.requestContent,
        requestedBy: user?.cid,
        status: "Pending"
      };

      const response = await apiService.post(config.CREATEAMENDMENT, payload);
      
      if (response && response.success) {
        await getAmendmentHistory();
        setActiveTab("history");
        if (onSuccess) onSuccess();
      } else {
        alert(response?.message || "Failed to submit amendment request");
      }
    } catch (error) {
      console.error("Error submitting amendment:", error);
      alert("Error submitting amendment request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendReply = async (flightAmendmentId) => {
    const messageText = replyTexts[flightAmendmentId]?.trim();
    if (!messageText) return;

    setSendingReplies(prev => ({ ...prev, [flightAmendmentId]: true }));
    try {
      const payload = {
        flightAmendmentId: flightAmendmentId,
        senderId: user?.id,
        senderType: "CLIENT", 
        messageText: messageText
      };

     
      const response = await apiService.post(config.ADDAMENDMENTMESSAGE, payload);

      if (response && response.success) {
        
        setReplyTexts(prev => ({ ...prev, [flightAmendmentId]: "" }));
        await getAmendmentHistory();
      } else {
        alert(response?.message || "Failed to send message");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Error sending message");
    } finally {
      setSendingReplies(prev => ({ ...prev, [flightAmendmentId]: false }));
    }
  };

  // Status Badge Helper
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "bg-success text-white";
      case "rejected":
        return "bg-danger text-white";
      default:
        return "bg-warning text-dark";
    }
  };

  if (!show || !booking) return null;

  return (
    <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }} tabIndex="-1">
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title fw-bold text-white">
              <i className="ri-edit-line me-2"></i>
              Manage Amendment Requests
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          
          <div className="modal-body">
            {/* Booking Information Banner */}
            <div className="alert alert-light border mb-3">
              <div className="row">
                <div className="col-md-6 border-end">
                  <span className="text-muted small">Booking Info</span>
                  <div><strong>Booking No:</strong> {booking.bookingNo}</div>
                  <div><strong>PNR:</strong> {booking.pnr}</div>
                </div>
                <div className="col-md-6 ps-md-3">
                  <span className="text-muted small">Flight & Traveller</span>
                  <div><strong>Traveller:</strong> {booking.travellerName}</div>
                  <div><strong>Journey:</strong> {booking.flightSearchRequest?.journeyType || "Flight"}</div>
                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <ul className="nav nav-tabs mb-3" role="tablist">
              <li className="nav-item">
                <button 
                  className={`nav-link fw-bold ${activeTab === "new" ? "active text-warning border-bottom border-warning border-3" : "text-muted"}`}
                  onClick={() => setActiveTab("new")}
                >
                  <i className="ri-add-circle-line me-1"></i> New Request
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link fw-bold ${activeTab === "history" ? "active text-warning border-bottom border-warning border-3" : "text-muted"}`}
                  onClick={() => setActiveTab("history")}
                >
                  <i className="ri-history-line me-1"></i> Request History ({existingAmendments.length})
                </button>
              </li>
            </ul>

            {activeTab === "new" ? (
              <form onSubmit={(e) => e.preventDefault()}>
                {/* Request Type Dropdown */}
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Request Type <span className="text-danger">*</span>
                  </label>
                  <select 
                    className="form-select" 
                    name="requestType"
                    value={amendmentRequest.requestType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Request Type --</option>
                    <option value="DateChange">📅 Date Change</option>
                    <option value="PassengerNameChange">👤 Passenger Name Change</option>
                    <option value="Cancellation">❌ Cancellation</option>
                    <option value="Refund">💰 Refund Request</option>
                    <option value="RouteChange">🛫 Route Change</option>
                    <option value="SeatUpgrade">💺 Seat Upgrade</option>
                    <option value="BaggageAdd">🧳 Add Baggage</option>
                    <option value="MealPreference">🍽️ Meal Preference</option>
                    <option value="SpecialAssistance">♿ Special Assistance</option>
                  </select>
                </div>

                {/* Request Content Textarea */}
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Request Details / Requirements <span className="text-danger">*</span>
                  </label>
                  <textarea 
                    className="form-control" 
                    rows="5"
                    name="requestContent"
                    value={amendmentRequest.requestContent}
                    onChange={handleChange}
                    placeholder="Specify target dates, passenger names, flight numbers, or routes you want adjusted."
                    required
                  ></textarea>
                </div>
              </form>
            ) : (
              <div style={{ maxHeight: "400px", overflowY: "auto" }} className="pe-2">
                {loadingHistory ? (
                  <div className="text-center py-4">
                    <span className="spinner-border text-warning"></span>
                    <p className="mt-2 text-muted">Retrieving amendment records...</p>
                  </div>
                ) : existingAmendments.length > 0 ? (
                  existingAmendments.map((amend, index) => {
                    const history = amend.messageHistory || amend.MessageHistory || [];
                    const isExpanded = expandedId === amend.flightAmendmentId;
                    
                    return (
                      <div className="card mb-2 border shadow-sm" key={amend.flightAmendmentId || index}>
                        {/* Interactive Clickable Header Bar */}
                        <div 
                          className="card-header d-flex justify-content-between align-items-center bg-light" 
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={() => toggleExpand(amend.flightAmendmentId)}
                        >
                          <div className="d-flex align-items-center gap-3">
                            <span className="fw-bold text-dark">
                              Request #{existingAmendments.length - index} ({amend.requestType})
                            </span>
                            <span className="text-muted small d-none d-sm-inline">
                              <RiCalendarLine className="me-1" />
                              {new Date(amend.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <span className={`badge ${getStatusBadgeClass(amend.status)}`}>
                              {amend.status}
                            </span>
                            {isExpanded ? (
                              <RiArrowUpSLine size={20} className="text-muted" />
                            ) : (
                              <RiArrowDownSLine size={20} className="text-muted" />
                            )}
                          </div>
                        </div>

                        {/* Collapsible Body Section */}
                        {isExpanded && (
                          <div className="card-body bg-white border-top">
                            <div className="p-2 bg-soft-warning border-start border-warning border-3 mb-3 rounded">
                              <strong className="small d-block mb-1">Initial Request Details:</strong>
                              <p className="mb-0 text-muted small">{amend.requestContent}</p>
                            </div>

                            {/* Two-Way Chat Conversation Thread */}
                            <div className="border rounded bg-light p-3 mb-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
                              <span className="text-muted small d-block mb-2 border-bottom pb-1 fw-semibold">
                                <RiMessage2Line className="me-1" /> Conversation Thread
                              </span>
                              
                              {history.length > 0 ? (
                                history.map((msg, mIndex) => {
                                  const isSupport = msg.senderType === "SUPPORT";
                                  return (
                                    <div key={msg.messageId || mIndex} className={`d-flex flex-column mb-3 ${isSupport ? "align-items-start" : "align-items-end"}`}>
                                      <div 
                                        className={`p-2 rounded-3 text-break ${isSupport ? "bg-white text-dark border-start border-primary border-3 shadow-sm" : "bg-warning-subtle text-dark border border-warning-subtle"}`} 
                                        style={{ maxWidth: "80%" }}
                                      >
                                        <div className="small fw-bold mb-1" style={{ fontSize: "10px" }}>
                                          {isSupport ? (
                                            <span className="text-primary"><RiCustomerServiceLine className="me-1" />SUPPORT</span>
                                          ) : (
                                            <span className="text-muted"><RiUserLine className="me-1" />YOU</span>
                                          )}
                                        </div>
                                        <div className="small">{msg.messageText}</div>
                                      </div>
                                      <small className="text-muted mt-1" style={{ fontSize: "9px" }}>
                                        {new Date(msg.createdAt).toLocaleString()}
                                      </small>
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="text-center py-3 text-muted small">
                                  No discussion thread started yet. Type below to send a message.
                                </div>
                              )}
                            </div>

                            {/* Reply Input (Disabled if request is already approved/rejected) */}
                            {amend.status?.toLowerCase() !== "approved" && amend.status?.toLowerCase() !== "rejected" && (
                              <div className="input-group mt-2">
                                <input 
                                  type="text" 
                                  className="form-control" 
                                  placeholder="Send a message or reply to support..."
                                  value={replyTexts[amend.flightAmendmentId] || ""}
                                  onChange={(e) => handleReplyChange(amend.flightAmendmentId, e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleSendReply(amend.flightAmendmentId);
                                    }
                                  }}
                                />
                                <button 
                                  className="btn btn-warning" 
                                  type="button"
                                  onClick={() => handleSendReply(amend.flightAmendmentId)}
                                  disabled={sendingReplies[amend.flightAmendmentId] || !replyTexts[amend.flightAmendmentId]?.trim()}
                                >
                                  {sendingReplies[amend.flightAmendmentId] ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                  ) : (
                                    <RiSendPlaneLine />
                                  )}
                                </button>
                              </div>
                            )}

                            {/* Static Admin Close Remarks (If present) */}
                            {amend.adminRemarks && (
                              <div className="bg-light p-2 rounded border-start border-3 border-secondary mt-2 small text-muted">
                                <strong>Admin Close Note:</strong> {amend.adminRemarks}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-4 text-muted">
                    No amendment requests found for this booking.
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="modal-footer">
            <button type="button" className="btn btn-danger " onClick={onClose}>
              Close
            </button>
            {activeTab === "new" && (
              <button 
                type="button" 
                className="btn btn-primary fw-bold text-white" 
                onClick={handleSubmit}
                disabled={submitting || !amendmentRequest.requestType || !amendmentRequest.requestContent}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>
                    Submitting...
                  </>
                ) : (
                  <>
                    <RiSendPlaneLine className="me-1" />
                    Submit Request
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightAmendmentModal;